<script setup lang="ts">
  import { computed, onMounted, onUnmounted, provide, watch } from 'vue'
  import type { StakeholderType } from '@gat-ship/types'
  import { stakeholderKey } from '@gat-ship/types/injection-keys'
  import { useCustomFields } from '@gat-ship/custom-fields'
  import { useFieldConfigurationSignalREvents } from '@gat-ship/signalr'
  import { useStakeholder } from '../../composables/useStakeholder'
  import { useStakeholderTarget } from '@gat-ship/stakeholders'
  import { provideStakeholderDetail } from './stakeholder-detail-context'
  import StakeholderModalProvider from './StakeholderModalProvider.vue'

  const props = withDefaults(
    defineProps<{
      stakeholderId?: string
      stakeholderType?: StakeholderType
      fetchOnMount?: boolean
    }>(),
    {
      fetchOnMount: true
    }
  )

  const stakeholderType = computed(() => props.stakeholderType)

  const service = useStakeholder(stakeholderType)

  const entityTarget = useStakeholderTarget(() => props.stakeholderType)
  const dynamicFields = useCustomFields(entityTarget)

  const fieldConfigSignalR = useFieldConfigurationSignalREvents({
    onCustomFieldValuesUpdated: (payload) => {
      if (payload.message.entityTargetId !== service.stakeholder.value?.id) {
        return
      }
      service.fetchCustomFieldValues(payload.message.entityTargetId)
    }
  })

  // Await the fetch so a failed load surfaces through Vue's global error
  // handler (the notification pipeline) instead of being silently swallowed.
  watch(
    () => props.stakeholderId,
    async (id) => {
      if (!id) {
        return
      }
      await service.fetchStakeholder(id)
    }
  )

  onMounted(async () => {
    service.subscribeToStakeholderEvents()
    fieldConfigSignalR.subscribeToFieldConfigurationEvents()
    if (props.fetchOnMount && props.stakeholderId) {
      await service.fetchStakeholder(props.stakeholderId)
    }
  })

  onUnmounted(() => {
    service.unsubscribeFromStakeholderEvents()
    fieldConfigSignalR.unsubscribeFromFieldConfigurationEvents()
  })

  provide(stakeholderKey, service)
  provideStakeholderDetail({ dynamicFields })

  defineExpose({
    datasource: service
  })
</script>

<template>
  <StakeholderModalProvider>
    <slot
      :stakeholder="service.stakeholder.value"
      :is-loading="service.isLoading.value"
    />
  </StakeholderModalProvider>
</template>
